import { getErrorCodeAndMessage } from '@data/global-api-errors';
import type { PatchGlobalApiPaymentAccountSelectionBody } from '@data/global-api-payments';
import {
  TOAST_VARIANT,
  useToast,
  useTranslationsContext,
} from '@state/checkout-contexts';
import { ChkProceedWithBankButton } from '@ui/checkout-components';
import { UiAccountSelectionDrawer } from '@ui/view-account-selection';
import { useDetection } from '@utility/checkout-utils';
import { COMMON_EVENTS } from '@utility/data-layer';

import { useScaPaymentDetails } from '../../hooks/useScaPaymentDetails/useScaPaymentDetails';
import { useInstitutionDetailsContext } from '../../providers/InstitutionDetailsContextProvider/InstitutionDetailsContextProvider';
import { dataLayer } from '../../utils/dataLayer';
import { TRANSLATION_KEYS } from '../../utils/TranslationKeys';

import type { AccountSelectionDrawerProps } from './AccountSelectionDrawer.types';

export const AccountSelectionDrawer = ({
  selectedAccountId,
}: AccountSelectionDrawerProps) => {
  const {
    paymentDetails,
    updatePaymentDetails,
    isFetching: isUpdatePaymentDetailsFetching,
  } = useScaPaymentDetails();

  const { bankName = '', iconUrl = '' } = useInstitutionDetailsContext();

  const { t } = useTranslationsContext();
  const { isMobile } = useDetection();

  const { setToast } = useToast();

  const patchPaymentWithSelectedAccount = async ({
    accountId,
  }: PatchGlobalApiPaymentAccountSelectionBody) => {
    if (isUpdatePaymentDetailsFetching) {
      return;
    }

    dataLayer.trackEvent({ name: COMMON_EVENTS.ACCOUNT_SELECTED });

    if (!paymentDetails) {
      return void dataLayer.trackEvent({
        name: COMMON_EVENTS.ACCOUNT_SELECTED_PAYMENT_DETAILS_UNDEFINED_WHILE_PATCH,
      });
    }

    const { error } = await updatePaymentDetails({
      openBankingEU: { accountId },
    });

    if (error) {
      const { errorMessage } = getErrorCodeAndMessage(error);
      setToast(errorMessage, TOAST_VARIANT.ERROR);

      return void dataLayer.trackEvent({
        name: COMMON_EVENTS.ACCOUNT_SELECTED_PATCH_ERROR,
      });
    }
  };

  return (
    <UiAccountSelectionDrawer.Drawer>
      {!isMobile && (
        <UiAccountSelectionDrawer.Header>
          {t(TRANSLATION_KEYS.CONTINUE_TO_YOUR_BANK)}
        </UiAccountSelectionDrawer.Header>
      )}
      <ChkProceedWithBankButton
        bankName={bankName}
        iconUrl={iconUrl}
        onClick={() =>
          patchPaymentWithSelectedAccount({ accountId: selectedAccountId })
        }
        disabled={isUpdatePaymentDetailsFetching}
      >
        {t(TRANSLATION_KEYS.CONTINUE)}
      </ChkProceedWithBankButton>
    </UiAccountSelectionDrawer.Drawer>
  );
};
